import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  DataTableColumnSettings,
  DataTableFilterButton,
  Icon,
  Text,
  useIsMobile,
  type BulkAction,
} from "@salestrak/ui";
import { PageHeader } from "#app/components/page-header";
import {
  TeamsTable,
  TEAMS_PINNED_COLUMN_IDS,
  buildTeamsFilterDefs,
  useTeamsColumns,
} from "#app/components/teams/teams-table";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { TEAM_LIST } from "#app/lib/filters/list-configs";
import type { TeamRow } from "#app/lib/data";
import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { useUserPool } from "#app/components/entity-selectors/hooks/use-user-pool";
import { renderTableEntityFilter } from "#app/components/entity-selectors/table-entity-filter";
import { settleAll } from "#app/lib/utils/settle-all";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useTableFilters } from "#app/lib/hooks/use-table-filters";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { ExpandableSearch } from "@salestrak/ui";
import { withPermission } from "#app/components/permissions/require-permission";

function SettingsTeamsPage() {
  const { push } = useAppNavigate();
  const isMobile = useIsMobile();
  const canCreateTeam = usePermission("manage.teams");

  const { pool: userPool } = useUserPool();
  const filterDefs = useMemo(() => buildTeamsFilterDefs(userPool.items), [userPool.items]);
  const teamFilters = useTableFilters(filterDefs);

  const { searchValue, setSearchValue, sorting, setSorting, where, orderBy } =
    useListQueryState(TEAM_LIST, { filterWhere: teamFilters.where });

  const { deleteTeam } = useTeamMutations();

  // --- Bulk actions config (settings variant only) ---
  const bulkActions = useMemo<BulkAction<TeamRow>[]>(
    () => [
      {
        id: "delete",
        type: "confirm",
        label: "Delete",
        icon: "trash-2",
        destructive: true,
        confirmTitle: (rows) =>
          `Delete ${rows.length} team${rows.length !== 1 ? "s" : ""}?`,
        confirmDescription: (rows) =>
          `This will permanently delete ${rows.length} team${rows.length !== 1 ? "s" : ""}. Issues assigned to these teams will be unassigned. This cannot be undone.`,
        confirmLabel: "Delete teams",
        onAction: async (selected) => {
          await settleAll(selected, (t) => deleteTeam(t.teamId));
        },
      },
    ],
    [deleteTeam],
  );

  // Dialog state
  const [deleteTarget, setDeleteTarget] = useState<{
    teamId: string;
    teamName: string;
  } | null>(null);

  const handleNavigateToTeam = useCallback(
    (teamId: string) => {
      push(`/settings/teams/${teamId}`);
    },
    [push],
  );

  const handleNavigateToTeamMembers = useCallback(
    (teamId: string) => {
      push(`/settings/teams/${teamId}/members`);
    },
    [push],
  );

  const handleDeleteTeam = useCallback((teamId: string, teamName: string) => {
    setDeleteTarget({ teamId, teamName });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteTeam(deleteTarget.teamId);
    setDeleteTarget(null);
  }, [deleteTarget, deleteTeam]);

  const handleCreateTeam = useCallback(() => {
    push("/settings/teams/create");
  }, [push]);

  const columns = useTeamsColumns({
    variant: "settings",
    onNavigateToTeam: handleNavigateToTeam,
    onNavigateToTeamMembers: handleNavigateToTeamMembers,
  });
  const tableView = useTableViewSettings("teams:settings");

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title="Teams"
        actions={
          <>
            <DataTableFilterButton
              filters={teamFilters.filters}
              activeFilterCount={teamFilters.activeFilterCount}
              clearAll={teamFilters.clearAll}
              setFilterValues={teamFilters.setFilterValues}
              renderEntityFilter={renderTableEntityFilter}
            />
            <ExpandableSearch
              onValueChange={setSearchValue}
              placeholder="Search teams..."
              size="xs"
            />
            <DataTableColumnSettings
              columns={columns}
              pinnedColumnIds={TEAMS_PINNED_COLUMN_IDS}
              {...tableView.settingsProps}
            />
            {canCreateTeam && (
              <Button
                size={isMobile ? "icon-xs" : "xs"}
                onClick={handleCreateTeam}
              >
                <Icon name="plus" />
                {!isMobile && <Text>Create team</Text>}
              </Button>
            )}
          </>
        }
      />
      <TeamsTable
        variant="settings"
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        columns={columns}
        tableViewProps={tableView.tableProps}
        onNavigateToTeam={handleNavigateToTeam}
        onNavigateToTeamMembers={handleNavigateToTeamMembers}
        onDeleteTeam={handleDeleteTeam}
        bulkActions={bulkActions}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete "{deleteTarget?.teamName}" team?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this team. Issues assigned to it will
              be unassigned. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              <Text>Cancel</Text>
            </AlertDialogClose>
            <Button onClick={confirmDelete}>
              <Text>Delete team</Text>
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/teams/")({
  component: withPermission("manage.teams", SettingsTeamsPage),
});
